import { useRef } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { axiosWithoutAuth } from "../../axios/axiosInstance";
import { isAlpha } from "../../Layout/Common/common";
import CustomPhoneField from "../../Layout/Common/customPhoneField";
import { FormikErrorMessage } from "../../Layout/Common/fromikErrorMessage";
import { useNoteAlert } from "../../Context/pleaseNote";
import { useAuth } from "../../Context/AuthContext";
import { PAGES } from "../../routeConfig";
import "../../assets/css/wes-contact-us.css";
import { GoogleRecaptcha } from "../../Layout/Common/googleRecaptcha";
import wesBuilding from "../../assets/img/about/wes-building.webp";

const topics = ["Product or stock", "Pricing or quote", "Existing order", "Trade account", "Something else"];

const directionsUrl =
  "https://www.google.com/maps/search/?api=1&query=84-90+Parramatta+Road,+Summer+Hill,+NSW+2130,+Australia";

const initialValues = {
  topic: topics[0],
  fullName: "",
  company: "",
  email: "",
  phone: "",
  message: "",
  recaptchaToken: "",
};

const validationSchema = Yup.object({
  fullName: Yup.string().trim().required("Full name is required"),
  company: Yup.string().trim().max(100, "Maximum 100 characters"),
  email: Yup.string().trim().email("Enter a valid email address").required("Email address is required"),
  phone: Yup.string()
    .matches(/^\d{7,15}$/, "Enter a valid phone number")
    .required("Phone number is required"),
  message: Yup.string().trim().max(600, "Maximum 600 characters").required("Please tell us how we can help"),
  recaptchaToken: Yup.string().required("Please confirm you are not a robot"),
});

const iconProps = {
  width: "20",
  height: "20",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

export const ContactUs = () => {
  const { showNoteAlert } = useNoteAlert();
  const { isAuthenticated } = useAuth();
  const isGuest = isAuthenticated === false;
  const recaptchaRef = useRef(null);

  const handleSubmit = async (values, { resetForm, setSubmitting, setFieldValue }) => {
    const data = {
      userId: String(sessionStorage.getItem("UserID")) == "null" ? "999" : String(sessionStorage.getItem("UserID")),
      fromid: values.email,
      familyName: "",
      currenturl: window.location.href,
      fullname: values.fullName,
      phone: values.phone,
      downloadrequire: values.message.trim(),
      topic: values.topic,
      company: values.company.trim(),
      recaptchaToken: values.recaptchaToken,
    };
    try {
      const response = await axiosWithoutAuth.post("MailSend/SendContactUs", data);
      if (response.data != "") {
        if (parseInt(response.data) == 1) {
          showNoteAlert("Thanks for your enquiry. Our Customer Sales Team will be in contact with you shortly.");
          resetForm();
        } else if (response.data == "-2") {
          showNoteAlert("Robot verification failed. Please try again.");
        } else {
          showNoteAlert("Mail send failed. Please try again.");
        }
      }
    } catch (error) {
      console.log(error.message);
    }
    recaptchaRef.current?.reset();
    setFieldValue("recaptchaToken", "");
    setSubmitting(false);
  };

  return (
    <main className="wes-contact-us-content hs-light">
      <section className="c-hero">
        <div className="wes-contact-us-style-1">
          <nav aria-label="Breadcrumb" className="c-muted wes-contact-us-style-2">
            <Link to="/" className="c-muted">
              Home
            </Link>
            <span aria-hidden="true">›</span>
            <span className="wes-contact-us-style-3">Contact Us</span>
          </nav>
          <div className="wes-contact-us-style-4">
            <div className="wes-contact-us-style-5">
              <span className="c-eyebrow wes-contact-us-style-6">Contact us</span>
              <h1 className="wes-contact-us-style-7">Talk to the WES team.</h1>
            </div>
          </div>
        </div>
      </section>

      <section className="c-hubsec wes-contact-us-style-8">
        <div className="wes-contact-us-style-9">
          <div className="wes-hub wes-contact-us-style-10">
            <div className="wes-contact-us-style-11">
              <h2 className="wes-contact-us-style-12">Get in touch</h2>
              <a href="tel:+61297979866" className="wes-contact-us-style-13">
                <span className="wes-contact-us-style-14">
                  <svg {...iconProps}>
                    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z"></path>
                  </svg>
                </span>
                <span className="wes-contact-us-style-15">
                  <span className="wes-contact-us-style-16">Phone</span>
                  <span className="wes-contact-us-style-17">(02) 9797 9866</span>
                  <span className="wes-contact-us-style-18">Fax (02) 9716 6015</span>
                </span>
              </a>
              <a href="mailto:sales@wes.net.au" className="wes-contact-us-style-13">
                <span className="wes-contact-us-style-14">
                  <svg {...iconProps}>
                    <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                    <path d="M22 6l-10 7L2 6"></path>
                  </svg>
                </span>
                <span className="wes-contact-us-style-15">
                  <span className="wes-contact-us-style-16">Email</span>
                  <span className="wes-contact-us-style-19">sales@wes.net.au</span>
                </span>
              </a>
              <a
                href="https://wa.me/61297979866"
                target="_blank"
                rel="noopener noreferrer"
                className="wes-contact-us-style-13"
              >
                <span className="wes-contact-us-style-14">
                  <svg {...iconProps}>
                    <path d="M21 11.5a8.4 8.4 0 0 1-12.4 7.4L3 21l2.1-5.4A8.4 8.4 0 1 1 21 11.5z"></path>
                    <path d="M9 9.5c.3 1.8 2.7 4.2 4.5 4.5l1.2-1.2 1.8.9-.5 1.5c-3 .3-7.5-4.2-7.2-7.2l1.5-.5.9 1.8z"></path>
                  </svg>
                </span>
                <span className="wes-contact-us-style-15">
                  <span className="wes-contact-us-style-16">WhatsApp</span>
                  <span className="wes-contact-us-style-19">+61 2 9797 9866</span>
                  <span className="wes-contact-us-style-18">Message us during business hours</span>
                </span>
              </a>
              <div className="wes-contact-us-style-20">
                <span className="wes-contact-us-style-14">
                  <svg {...iconProps}>
                    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z"></path>
                    <circle cx="12" cy="9.5" r="2.5"></circle>
                  </svg>
                </span>
                <span className="wes-contact-us-style-15">
                  <span className="wes-contact-us-style-16">Trade counter</span>
                  <a
                    className="wes-contact-us-style-13"
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="wes-contact-us-style-21">
                      84-90 Parramatta Road,
                      <br />
                      Summer Hill, NSW, 2130
                      <br />
                      Australia
                    </span>
                  </a>
                </span>
              </div>
              <div className="wes-contact-us-style-20">
                <span className="wes-contact-us-style-14">
                  <svg {...iconProps}>
                    <circle cx="12" cy="12" r="9"></circle>
                    <path d="M12 7v5l3 2"></path>
                  </svg>
                </span>
                <span className="wes-contact-us-style-22">
                  <span className="wes-contact-us-style-16">Business hours</span>
                  <span>
                    <strong>Mon – Fri</strong> 8:30am – 5:00pm
                  </span>
                  <span>
                    <strong>Sat:</strong> 9:00am – 2:00pm{" "}
                    <span className="wes-contact-us-style-23">(trade counter only)</span>
                  </span>
                  <span>
                    <strong>Sun:</strong> Closed
                  </span>
                  <span>
                    <strong>Public holidays:</strong> Closed
                  </span>
                </span>
              </div>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              validateOnChange={true}
              validateOnBlur={true}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, setFieldValue }) => (
                <Form className="wes-contact-us-style-24" noValidate>
                  <div className="wes-contact-us-style-25">
                    <h2 className="wes-contact-us-style-12">Send us a message</h2>
                    <p className="wes-contact-us-style-26">
                      Send us your enquiry and one of our team will get back to you shortly.
                    </p>
                  </div>
                  <fieldset className="wes-contact-us-style-27">
                    <legend className="wes-contact-us-style-28">I need help with</legend>
                    <div className="wes-contact-us-style-29">
                      {topics.map((topic) => (
                        <label className="wes-chip" key={topic}>
                          <Field type="radio" name="topic" value={topic} /> {topic}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div className="wes-ind wes-contact-us-style-30">
                    <div className="wes-field">
                      <label htmlFor="b-name">Full name *</label>
                      <Field name="fullName">
                        {({ field }) => (
                          <input
                            {...field}
                            id="b-name"
                            type="text"
                            maxLength="40"
                            autoComplete="name"
                            onChange={(changeEvent) => setFieldValue("fullName", isAlpha(changeEvent.target.value))}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="fullName">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                    </div>
                    <div className="wes-field">
                      <label htmlFor="b-company">Company</label>
                      <Field id="b-company" name="company" type="text" maxLength="100" autoComplete="organization" />
                      <ErrorMessage name="company">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                    </div>
                    <div className="wes-field">
                      <label htmlFor="b-email">Email *</label>
                      <Field id="b-email" name="email" type="email" maxLength="40" autoComplete="email" />
                      <ErrorMessage name="email">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                    </div>
                    <div className="wes-field">
                      <label htmlFor="b-phone">Phone or mobile *</label>
                      <Field id="b-phone" name="phone" component={CustomPhoneField} maxLength={15} />
                      <ErrorMessage name="phone">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                    </div>
                  </div>
                  <div className="wes-field">
                    <label htmlFor="b-msg">Message *</label>
                    <Field
                      as="textarea"
                      id="b-msg"
                      name="message"
                      rows="4"
                      maxLength="600"
                      placeholder="Include part numbers or quantities if you have them"
                    />
                    <ErrorMessage name="message">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                  </div>
                  <div>
                    <GoogleRecaptcha ref={recaptchaRef} onChange={(token) => setFieldValue("recaptchaToken", token)} />
                    <ErrorMessage name="recaptchaToken">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                  </div>
                  <button type="submit" className="wes-contact-us-style-35" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Send message →"}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </section>

      <section>
        <div className="wes-contact-us-style-36">
          <div className="wes-contact-us-style-37">
            <div className="wes-contact-us-style-38">
              <span className="wes-contact-us-style-39">Visit us</span>
              <h2 className="wes-contact-us-style-40">Pick up from Summer Hill</h2>
              <p className="wes-contact-us-style-41">
                Centrally located in Summer Hill, NSW, our trade counter is a convenient stop for Sydney-based customers
                to pick up orders and talk to our team in person.
              </p>
            </div>
            <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="wes-contact-us-style-42">
              <svg {...iconProps} width="18" height="18">
                <path d="M3 11l19-9-9 19-2-8-8-2z"></path>
              </svg>
              Get directions
            </a>
          </div>
          <div className="wes-contact-us-style-43">
            <img
              className="wes-wide wes-contact-us-style-44"
              src={wesBuilding}
              alt="WES Australasia warehouse and trade counter at 84-90 Parramatta Road, Summer Hill"
              loading="lazy"
            />
            <iframe
              className="wes-wide wes-map wes-contact-us-style-45"
              title="Map of WES Australasia, 84-90 Parramatta Road, Summer Hill NSW 2130"
              src="https://maps.google.com/maps?q=84-90%20Parramatta%20Road%2C%20Summer%20Hill%20NSW%202130&z=16&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {isGuest && (
        <section className="wes-contact-us-style-46">
          <div className="wes-contact-us-style-47">
            <div className="wes-contact-us-style-48">
              <div className="wes-contact-us-style-49">
                <h2 className="wes-contact-us-style-50">Not a WES customer yet?</h2>
                <p className="wes-contact-us-style-51">
                  Open a trade account for online ordering, account pricing and order history.
                </p>
              </div>
              <Link to={PAGES.SIGNUP} className="wes-contact-us-style-52">
                Open a trade account
              </Link>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};
